/**
 * SmartPersonalizer — detects the visitor's province via browser timezone
 * (zero PII, no IP lookup) and slips a soft banner above the hero that
 * deep-links to their province-specific software page. Drops a small
 * personalised line under the hero headline.
 *
 * Why timezone, not IP geolocation? IP geo requires a paid 3rd-party API
 * call on every pageload + a network round-trip. Timezone is 100% local,
 * zero-latency, and accurate to within ~province boundaries for Canada.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, MapPin, X as XIcon } from "lucide-react";
import { Link } from "react-router-dom";

const DISMISS_KEY = "cr_personalizer_dismissed";

// Maps Canadian IANA timezones to our province software-page slugs.
const TZ_TO_PROVINCE = {
  "America/Toronto":       { code: "ON", name: "Ontario",                  slug: "ontario" },
  "America/Montreal":      { code: "QC", name: "Quebec",                   slug: "quebec" },
  "America/Vancouver":     { code: "BC", name: "British Columbia",         slug: "british-columbia" },
  "America/Edmonton":      { code: "AB", name: "Alberta",                  slug: "alberta" },
  "America/Calgary":       { code: "AB", name: "Alberta",                  slug: "alberta" },
  "America/Winnipeg":      { code: "MB", name: "Manitoba",                 slug: "manitoba" },
  "America/Regina":        { code: "SK", name: "Saskatchewan",             slug: "saskatchewan" },
  "America/Halifax":       { code: "NS", name: "Nova Scotia",              slug: "nova-scotia" },
  "America/Moncton":       { code: "NB", name: "New Brunswick",            slug: "new-brunswick" },
  "America/St_Johns":      { code: "NL", name: "Newfoundland & Labrador",  slug: "newfoundland-and-labrador" },
  "America/Whitehorse":    { code: "YT", name: "Yukon",                    slug: "yukon" },
  "America/Yellowknife":   { code: "NT", name: "Northwest Territories",    slug: "northwest-territories" },
  "America/Iqaluit":       { code: "NU", name: "Nunavut",                  slug: "nunavut" },
};

function detectProvince() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TZ_TO_PROVINCE[tz] || null;
  } catch {
    return null;
  }
}

export { detectProvince };
export default function SmartPersonalizer() {
  const province = useMemo(detectProvince, []);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
  }, []);

  if (!province || dismissed) return null;

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  }

  return (
    <div
      data-testid="smart-personalizer-banner"
      className="bg-gradient-to-r from-emerald-50 via-white to-sky-50 border-b border-emerald-100"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="w-4 h-4 text-emerald-700 flex-none"/>
          <span className="text-slate-700 truncate">
            <b className="font-semibold text-slate-900">{province.name}</b> landlord?
            <span className="hidden sm:inline"> See how we handle the LTB / RTA / TAL specifics for your province.</span>
          </span>
        </div>
        <div className="flex items-center gap-2 flex-none">
          <Link
            to={`/landlord-software/${province.slug}`}
            data-testid="smart-personalizer-cta"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition-colors"
          >
            For {province.code} landlords <ArrowRight className="w-3 h-3"/>
          </Link>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            data-testid="smart-personalizer-dismiss"
            className="p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700"
          >
            <XIcon className="w-4 h-4"/>
          </button>
        </div>
      </div>
    </div>
  );
}
